import { useState } from 'react';
import { Key, Trash, Copy, Check, X, Loader2, RefreshCw, } from 'lucide-react';
import { Label } from '@radix-ui/react-label';
import { Input } from '@/components/ui/input';
import { toast } from '@/hooks/use-toast';
import { api } from '@/lib/api-client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/providers/AuthProvider';
import { createFileRoute } from '@tanstack/react-router';
import { Checkbox } from '@/components/ui/checkbox';

export const Route = createFileRoute('/key-management/api-keys')({
  component: APIKeysTab,
});

export function APIKeysTab() {
  const [keyName, setKeyName] = useState('');
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [keyToRevoke, setKeyToRevoke] = useState<any | null>(null);
  const [keyToRotate, setKeyToRotate] = useState<any | null>(null); // New state
  const [keepOldKey, setKeepOldKey] = useState(false); // New state
  const [viewPublicKey, setViewPublicKey] = useState<any | null>(null);
  const { isReady } = useAuth();
  const queryClient = useQueryClient();

  // GET - Fetch existing keys
  const { data, isLoading: isListLoading } = useQuery({
    queryKey: ['apiKeys'],
    queryFn: () => api.apiKeyManagement.listKeys(),
    enabled: isReady,
  });

  const activeKeys = data?.data || [];

  // POST - Create a Key
  const createMutation = useMutation({
    mutationFn: (name: string) => api.apiKeyManagement.createKey(name),
    onSuccess: (result) => {
      if (result.success) {
        setGeneratedKey(result.data.fullKey);
        setKeyName('');
        queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
        toast({ title: "API Key created successfully" });
      }
    },
    onError: (error: any) => {
      toast({ title: error?.message || "Failed to generate key", variant: "destructive" });
    }
  });

  // POST - Rotate a Key
  const rotateMutation = useMutation({
    mutationFn: ({ keyId, options }: { keyId: string, options: { keepOldKey: boolean } }) => 
      api.apiKeyManagement.rotateKey(keyId, options),
    onSuccess: (result: any) => {

      const newKeyString = result?.data?.newKey?.fullKey;
      
      if (newKeyString) {
        setGeneratedKey(newKeyString); 
        
        queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
        
        setKeyToRotate(null);
        setKeepOldKey(false);
        
        toast({ title: "API Key rotated successfully" });
      } else {
        console.error("Key rotation response structure mismatch:", result);
        toast({ 
          title: "Rotation partially failed", 
          description: "The key was rotated on the server, but the frontend could not capture the new secret. Please check your key list.",
          variant: "destructive" 
        });
      }
    },
    onError: (error: any) => {
      toast({ 
        title: "Failed to rotate key", 
        description: error?.message || "Server error occurred",
        variant: "destructive" 
      });
    }
  });;

  // DELETE - Revoke a Key
  const revokeMutation = useMutation({
    mutationFn: (keyId: string) => api.apiKeyManagement.revokeKey(keyId),
    onSuccess: (result: any) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
        toast({ title: "API key revoked" });
      }
    },
    onError: () => {
      toast({ title: "Failed to revoke key", variant: "destructive" });
    }
  });

  const handleGenerate = () => {
    if (!keyName) {
      toast({ title: "Please enter a name for the API key", variant: "destructive" });
      return;
    }
    createMutation.mutate(keyName);
  };

  const confirmRevoke = () => {
    if (keyToRevoke) {
      revokeMutation.mutate(keyToRevoke.id);
      setKeyToRevoke(null); 
    }
  };

  const confirmRotate = () => {
    if (keyToRotate) {
      rotateMutation.mutate({ keyId: keyToRotate.id, options: { keepOldKey } });
    }
  };

  const copyToClipboard = () => {
    if (generatedKey) {
      navigator.clipboard.writeText(generatedKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-xl p-6 border border-border relative">
        <h2 className="font-medium mb-4">External API Access</h2>
        <p className="text-muted-foreground mb-6">
          Generate API keys to allow external applications to access your templates and generate reports.
        </p>

        <div className="mt-6 p-2">
          <Label htmlFor="api-key-name" className="text-base font-semibold mb-2 block">
            API Key Name *
          </Label>
          <div className="flex items-center gap-4">
            <Input
              id="api-key-name"
              placeholder="e.g. Production Server"
              value={keyName}
              onChange={(e) => setKeyName(e.target.value)}
              disabled={createMutation.isPending}
              className="border-2 rounded-xl flex-1 h-10"
            />
            <button 
              onClick={handleGenerate}
              disabled={createMutation.isPending}
              className="h-10 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors flex gap-2 items-center whitespace-nowrap disabled:opacity-50"
            >
              {createMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
              <span>Generate API Key</span>
            </button>
          </div>
        </div>

        {/* New Key Display Modal (Used for Create & Rotate) */}
        {generatedKey && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center backdrop-blur-sm bg-background/80 p-4">
            <div className="bg-card p-6 rounded-2xl border border-border shadow-2xl max-w-lg w-full animate-in fade-in zoom-in duration-200">
              <div className="flex justify-between items-start mb-2">
                <h3 className="text-xl font-bold text-foreground">New API Key Generated</h3>
                <button onClick={() => setGeneratedKey(null)} className="text-muted-foreground hover:text-foreground">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-muted-foreground text-sm mb-6">
                Copy your new API key now. For security reasons, it won't be shown again.
              </p>

              <div className="flex items-center justify-between bg-muted/50 p-4 rounded-xl border border-border font-mono text-sm break-all mb-6">
                <span className="truncate mr-4 text-[#FF4500] font-bold">{generatedKey}</span>
                <button onClick={copyToClipboard} className="p-2 hover:bg-muted rounded-md transition-colors shrink-0">
                  {copied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5 text-muted-foreground" />}
                </button>
              </div>

              <div className="flex justify-end">
                <button 
                  onClick={() => setGeneratedKey(null)}
                  className="bg-primary hover:bg-primary/90 text-white font-bold py-3 px-6 rounded-xl transition-colors w-full sm:w-auto"
                >
                  I've Copied the Key
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="mt-8">
          <h3 className="font-medium mb-3">Active API Keys</h3>
          <div className="space-y-3">
            {isListLoading ? (
              <div className="flex justify-center p-8"><Loader2 className="animate-spin text-muted-foreground" /></div>
            ) : activeKeys.length === 0 ? (
              <div className="text-center p-8 border border-dashed rounded-lg text-muted-foreground">
                No active API keys found.
              </div>
            ) : (
              activeKeys.map((key: any) => (
                <div key={key.id} className="p-4 border border-border rounded-lg flex items-center justify-between bg-muted/20">
                  <div>
                    <p className="font-medium">{key.name}</p>
                    <p className="text-xs font-mono text-muted-foreground">
                      Prefix: {key.publicKey} • Created {new Date(key.createdAt).toLocaleDateString()}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Last used: {key.lastUsedAt ? new Date(key.lastUsedAt).toLocaleString() : 'Never'}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => setViewPublicKey(key)}
                      className="p-2 hover:bg-muted rounded-md transition-colors"
                      title="View Identifier"
                    >
                      <Key className="w-4 h-4 text-muted-foreground" />
                    </button>
                    <button 
                      onClick={() => setKeyToRotate(key)}
                      disabled={rotateMutation.isPending}
                      className="h-9 px-3 text-primary hover:bg-primary/10 rounded-md transition-colors flex gap-2 items-center text-sm font-medium"
                    >
                      <RefreshCw className={`w-4 h-4 ${rotateMutation.isPending && rotateMutation.variables?.keyId === key.id ? 'animate-spin' : ''}`} />
                      Rotate
                    </button>
                    <button 
                      onClick={() => setKeyToRevoke(key)}
                      disabled={revokeMutation.isPending}
                      className="h-9 px-3 text-destructive hover:bg-destructive/10 rounded-md transition-colors flex gap-2 items-center text-sm font-medium"
                    >
                      {revokeMutation.isPending && revokeMutation.variables === key.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash className="w-4 h-4" />}
                      Revoke
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Rotate Modal */}
        {keyToRotate && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={() => setKeyToRotate(null)} />
            <div className="relative bg-card border border-border w-full max-w-md rounded-2xl p-6 shadow-2xl animate-in fade-in zoom-in duration-200">
              <div className="flex items-center gap-3 mb-4 text-primary">
                <div className="p-2 bg-primary/10 rounded-full">
                  <RefreshCw className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-foreground">Rotate API Key</h3>
              </div>
              
              <p className="text-muted-foreground text-sm mb-4 leading-relaxed">
                Rotating <span className="font-semibold text-foreground">"{keyToRotate.name}"</span> will generate a new secret.
              </p>

              <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mb-6">
                <div className="flex gap-3">
                  <Checkbox 
                    id="keepOldKey" 
                    checked={keepOldKey} 
                    onCheckedChange={(checked) => setKeepOldKey(!!checked)} 
                    className="mt-1"
                  />
                  <div className="grid gap-1.5 leading-none">
                    <label htmlFor="keepOldKey" className="text-sm font-bold flex items-center gap-2">
                      Enable Grace Period
                    </label>
                    <p className="text-xs text-muted-foreground">
                      Keep the old key active temporarily so your services don't break while you update the new key.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-3">
                <button onClick={() => setKeyToRotate(null)} className="px-4 py-2 rounded-xl text-sm font-medium hover:bg-muted transition-colors border border-border">
                  Cancel
                </button>
                <button 
                  onClick={confirmRotate}
                  disabled={rotateMutation.isPending}
                  className="px-4 py-2 bg-primary text-white rounded-xl text-sm font-bold hover:bg-primary/90 transition-all flex items-center justify-center gap-2"
                >
                  {rotateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  Confirm Rotation
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Revoke Modal */}
        {keyToRevoke && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={() => setKeyToRevoke(null)} />
            <div className="relative bg-card border border-border w-full max-w-md rounded-2xl p-6 shadow-2xl animate-in fade-in zoom-in duration-200">
              <div className="flex items-center gap-3 mb-4 text-destructive">
                <div className="p-2 bg-destructive/10 rounded-full">
                  <Trash className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-foreground">Revoke API Key</h3>
              </div>
              
              <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
                Are you sure you want to revoke the key: <span className="font-semibold text-foreground">" {keyToRevoke.name} "</span> ? 
                Applications using this key will lose access <span className="text-destructive font-bold">immediately</span>. This cannot be undone.
              </p>

              <div className="flex flex-col sm:flex-row justify-end gap-3">
                <button onClick={() => setKeyToRevoke(null)} className="px-4 py-2 rounded-xl text-sm font-medium hover:bg-muted transition-colors border border-border">
                  Cancel
                </button>
                <button onClick={confirmRevoke} className="px-4 py-2 bg-destructive text-white rounded-xl text-sm font-bold hover:bg-destructive/90 transition-all shadow-lg shadow-destructive/20">
                  Confirm Revocation
                </button>
              </div>
            </div>
          </div>
        )}

        {/* View Public Key Modal */}
        {viewPublicKey && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={() => setViewPublicKey(null)} />
            <div className="relative bg-card border border-border w-full max-w-md rounded-2xl p-6 shadow-2xl animate-in fade-in zoom-in duration-200">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">API Key Details</h3>
                <button onClick={() => setViewPublicKey(null)}><X className="w-5 h-5 text-muted-foreground" /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs uppercase text-muted-foreground font-bold">Key Name</Label>
                  <p className="font-medium">{viewPublicKey.name}</p>
                </div>
                <div>
                  <Label className="text-xs uppercase text-muted-foreground font-bold">Public Identifier (Prefix)</Label>
                  <div className="mt-1 flex items-center justify-between bg-muted/50 p-3 rounded-lg border border-border font-mono text-sm">
                    <span className="text-primary truncate mr-2">{viewPublicKey.publicKey}</span>
                    <button onClick={() => { navigator.clipboard.writeText(viewPublicKey.publicKey); toast({ title: "Prefix copied!" }); }} className="p-1.5 hover:bg-muted rounded-md">
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-[12px] text-muted-foreground mt-2">
                    * Use this identifier to find this key in your logs or server code.
                  </p>
                </div>
              </div>
              <button onClick={() => setViewPublicKey(null)} className="mt-6 w-full py-2 bg-secondary hover:bg-secondary/80 rounded-xl font-medium transition-colors">
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}